import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Isvg from 'react-inlinesvg';
import { addDays, parseISO, startOfDay } from 'date-fns';

import { createErrorBanner } from 'components/Banner';
import Dot from 'components/Dot';
import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Dropdown, Form } from 'components/HookForm';
import Message from 'components/Message';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar/Snackbar.saga';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import LINKS from 'constants/links';
import URLS from 'constants/urls';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { bem } from 'helpers/class-name';
import { transformKeysToSnakeCase, transformV2ClientResponse } from 'helpers/core';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { adjustDateForTimezone, zonedTimeToUtc } from 'helpers/intl-fns';
import segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';

import { VENUE_OPEN_STATUS, VENUE_OPEN_STATUS_MAPPING, VENUE_OPEN_STATUS_OPTIONS } from '../Items.constants';
import { venueOpenStatusMessages as messages } from '../Items.messages';

import './VenueOpenStatus.scss';
import { VENUE_CLOSED_CONFIRMATION_MODAL_NAME } from './VenueClosedConfirmationModal';

const { block, element } = bem('venue-open-status');
const FORM_NAME = 'venue-open-status-form';

const VenueOpenStatus = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [venueOpenStatus, setVenueOpenStatus] = useState(null);
  const [existingDisabledWindow, setExistingDisabledWindow] = useState(null);

  const { enabled, hideInSearch, venueId, timezone } = useSelector(selectActiveVenue);
  const isVenueHidden = !enabled || hideInSearch;
  const currentDayInVenueTimezone = startOfDay(adjustDateForTimezone(new Date(), timezone));
  const nextDayInVenueTimezone = addDays(currentDayInVenueTimezone, 1);

  const handleCloseModal = () => dispatch(closeModal());

  const fetchVenueDisabledTimeWindows = async () => {
    try {
      const response = await httpClient.get(
        URLS.venue.getDisabledTimeWindows({ venueId, endsAfter: new Date().toISOString() })
      );
      const [disabledTimeWindow] = transformV2ClientResponse(response);

      if (disabledTimeWindow) {
        const { startTime, endTime } = disabledTimeWindow;
        const currentDateInVenueTimezone = adjustDateForTimezone(new Date(), timezone);
        const existingStartTimeInVenueTimezone = adjustDateForTimezone(parseISO(startTime), timezone);
        const existingEndTimeInVenueTimezone = adjustDateForTimezone(parseISO(endTime), timezone);

        if (
          currentDateInVenueTimezone >= existingStartTimeInVenueTimezone &&
          currentDateInVenueTimezone <= existingEndTimeInVenueTimezone
        ) {
          setVenueOpenStatus(VENUE_OPEN_STATUS.CLOSED);
          setExistingDisabledWindow({
            ...disabledTimeWindow,
            startTime: existingStartTimeInVenueTimezone,
            endTime: existingEndTimeInVenueTimezone,
          });
        } else {
          setVenueOpenStatus(VENUE_OPEN_STATUS.OPEN);
        }
      } else {
        setVenueOpenStatus(VENUE_OPEN_STATUS.OPEN);
      }
    } catch (error) {
      reportError(error);
      dispatch(createErrorBanner(messages.venueOpenStatusLoadError));
    }
    setIsLoading(false);
  };

  const createVenueDisabledTimeWindow = async (startTime, endTime, description) => {
    const utcStartTime = zonedTimeToUtc(startTime, timezone).toISOString();
    const utcEndTime = zonedTimeToUtc(endTime, timezone).toISOString();
    const payload = transformKeysToSnakeCase({
      startTime: utcStartTime,
      endTime: utcEndTime,
      description,
      venueId,
    });
    await httpClient.post(URLS.venue.createDisabledTimeWindow({ venueId }), payload);
  };

  const openVenueClosedConfirmationModal = () => {
    return new Promise(resolve => {
      const handleCancel = () => resolve(false);
      const handleConfirm = () => {
        resolve(true);
        segment.event('Venue Closed for day', {
          venueId,
          dateOfClosure: currentDayInVenueTimezone.toISOString(),
        });
        braze.trackEvent(BRAZE_CUSTOM_EVENTS.ADDED_CLOSED_DATES);
      };

      dispatch(
        openModal(VENUE_CLOSED_CONFIRMATION_MODAL_NAME, {
          handleCancel,
          handleConfirm,
        })
      );
    });
  };

  const handleSetVenueOpen = async () => {
    if (existingDisabledWindow) {
      const { id, description, endTime } = existingDisabledWindow;
      await httpClient.delete(URLS.venue.deleteDisabledTimeWindow({ venueId, timeWindowId: id }));

      //  If an existing disabled window extends beyond the next day, we need to create a new
      //  disabled window for the remaining days after deleting the window for today.
      if (endTime > nextDayInVenueTimezone) {
        await createVenueDisabledTimeWindow(nextDayInVenueTimezone, endTime, description);
      }
    }
  };

  function showCloseSuccessSnackbar() {
    dispatch(createSuccessSnackbar(messages.storeClosedSuccess));
  }

  function showOpenSuccessSnackbar() {
    dispatch(createSuccessSnackbar(messages.storeOpenSuccess));
  }

  const itemsContactLinkRenderer = chunks => (
    <a
      href={LINKS.contact.partnerSuccessHref}
      target="_blank"
      rel="noreferrer noopener"
      className={element('snackbar-link')}>
      {chunks}
    </a>
  );

  function showCloseErrorSnackbar(error) {
    dispatch(
      createErrorSnackbar(
        messages.storeClosedErrorTitle,
        {
          strong: chunks => <strong>{chunks}</strong>,
        },
        undefined,
        getApiErrorSnackbarDescription(error, itemsContactLinkRenderer)
      )
    );
  }

  function showOpenErrorSnackbar(error) {
    dispatch(
      createErrorSnackbar(
        messages.storeOpenErrorTitle,
        {
          strong: chunks => <strong>{chunks}</strong>,
        },
        undefined,
        getApiErrorSnackbarDescription(error, itemsContactLinkRenderer)
      )
    );
  }

  const onSubmit = async ({ venueOpenStatusInput }) => {
    if (venueOpenStatusInput === venueOpenStatus) {
      return;
    }

    if (venueOpenStatusInput === VENUE_OPEN_STATUS.CLOSED) {
      const confirmed = await openVenueClosedConfirmationModal();
      if (confirmed) {
        try {
          await createVenueDisabledTimeWindow(currentDayInVenueTimezone, nextDayInVenueTimezone, 'Closed for the day');

          showCloseSuccessSnackbar();
        } catch (error) {
          reportError(error);

          showCloseErrorSnackbar(error);
        }
      } else {
        handleCloseModal();

        return;
      }
      handleCloseModal();
    } else if (venueOpenStatusInput === VENUE_OPEN_STATUS.OPEN) {
      try {
        await handleSetVenueOpen();

        showOpenSuccessSnackbar();
      } catch (error) {
        reportError(error);

        showOpenErrorSnackbar(error);
      }
    }

    await fetchVenueDisabledTimeWindows();
  };

  useEffect(() => {
    fetchVenueDisabledTimeWindows();
  }, [venueId]);

  return (
    <div className={block()}>
      <Form id={FORM_NAME} isLoading={isLoading} onSubmit={onSubmit}>
        {({ handleSubmit }) => {
          return (
            <Dropdown
              className={element('dropdown')}
              name="venueOpenStatusInput"
              disabled={isVenueHidden}
              isAlignTopToggleStyle
              isLargeRadiusStyle
              showMenuCaret>
              {({ onChange }) => {
                return (
                  <>
                    <DropdownToggle>
                      <When predicate={isVenueHidden}>
                        <Isvg className={element('eye-icon')} src={ICONS.eyeWithCross} />
                        <Message message={messages.venueStatusHidden} />
                      </When>
                      <When predicate={!isVenueHidden}>
                        <Dot
                          className={element('status-marker')}
                          color={VENUE_OPEN_STATUS_MAPPING[venueOpenStatus]?.color}
                          largeSize
                        />
                        <Message message={messages[VENUE_OPEN_STATUS_MAPPING[venueOpenStatus]?.message]} />
                      </When>
                    </DropdownToggle>
                    <DropdownMenu className={element('dropdown-menu')}>
                      {VENUE_OPEN_STATUS_OPTIONS.sort((a, b) => {
                        if (a === venueOpenStatus) return -1;
                        if (b === venueOpenStatus) return 1;
                        return 0;
                      }).map(status => {
                        const { color, message } = VENUE_OPEN_STATUS_MAPPING[status];
                        return (
                          <DropdownItem
                            key={status}
                            isSelected={venueOpenStatus === status}
                            onClick={() => {
                              onChange(status);
                              handleSubmit(onSubmit)();
                            }}>
                            <Dot className={element('status-marker')} color={color} largeSize />
                            <Message message={messages[message]} />
                          </DropdownItem>
                        );
                      })}
                    </DropdownMenu>
                  </>
                );
              }}
            </Dropdown>
          );
        }}
      </Form>
    </div>
  );
};

export default VenueOpenStatus;
